import React from 'react';
import './CurriculumReportView.css';
import { sanitizePublicText } from '../utils/publicText';

function ElectiveSuggestionsTable({ courses }) {
  const suggestions = (courses || []).flatMap((course) => (
    (course.alt_ders_onerileri || [])
      .filter((suggestion) => suggestion.durum === 'yeni')
      .map((suggestion) => ({
        ...suggestion,
        parentCourseName: course.course_name,
      }))
  ));

  if (suggestions.length === 0) return null;

  return (
    <div className="curriculum-report__elective-suggestions">
      <h3>Seçmeli Ders Önerileri</h3>
      <div className="diff-table-container">
        <table className="diff-table">
          <thead>
            <tr>
              <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
              <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th><th>Durum</th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((course, index) => (
              <tr
                key={`${course.course_code || course.course_name}-${index}`}
                className="diff-row diff-row--elective-suggestion diff-row--added"
              >
                <td>{course.course_code}</td>
                <td>{course.course_name}</td>
                <td>{course.course_type || 'Seçmeli'}</td>
                <td>{course.teorik}</td>
                <td>{course.uygulama}</td>
                <td>{course.laboratuvar}</td>
                <td>{course.kredi}</td>
                <td>{course.akts}</td>
                <td>{course._secili || course.secili ? 'Plana eklendi' : 'Öneri'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CurriculumReportView({
  programName, departmentName, durationYear, educationLevel, donemComparisons, narrative,
  onBack, onDownloadPdf, downloadingPdf, downloadError,
}) {
  const yorumByDonem = new Map(
    (narrative?.donem_yorumlari || []).map((y) => [y.donem, y.yorum]),
  );

  return (
    <div className="curriculum-report">
      <div className="curriculum-report__toolbar">
        <button onClick={onBack} className="back-button">← Plana Geri Dön</button>
        <button
          onClick={onDownloadPdf}
          className="pdf-button"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'PDF Hazırlanıyor...' : 'PDF İndir'}
        </button>
      </div>

      {downloadError && (
        <div className="curriculum-report__error">{downloadError}</div>
      )}

      <div className="curriculum-report__page">
        <div className="curriculum-report__header">
          <div className="curriculum-report__university">Kocaeli Sağlık ve Teknoloji Üniversitesi</div>
          <h1 className="curriculum-report__title">{programName} - Ders Planı Güncelleme Raporu</h1>
          {(departmentName || educationLevel || durationYear) && (
            <div className="curriculum-report__subtitle">
              {[
                departmentName,
                educationLevel,
                durationYear && !['Yüksek Lisans', 'Doktora'].includes(educationLevel)
                  ? `${durationYear} Yıllık Program`
                  : null,
              ].filter(Boolean).join(' — ')}
            </div>
          )}
        </div>

        {donemComparisons.map((dc) => (
          <section className="curriculum-report__section" key={dc.donem}>
            <h2><span className="curriculum-report__donem-badge">{dc.donem}</span> Dönem</h2>

            {dc.eskiDersler.length > 0 && (
              <>
                <h3>Mevcut Plan</h3>
                <div className="diff-table-container">
                  <table className="diff-table">
                    <thead>
                      <tr>
                        <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                        <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dc.eskiDersler.map((c, i) => (
                        <React.Fragment key={i}>
                          <tr
                            className={
                              c._rowState === 'kaldirildi' ? 'diff-row diff-row--removed'
                              : c._rowState === 'güncellendi' ? 'diff-row diff-row--updated'
                              : 'diff-row diff-row--same'
                            }
                          >
                            <td>
                              {c.course_code}
                              {c._rowState === 'kaldirildi' && <span className="diff-badge diff-badge--removed">Kaldırıldı</span>}
                              {c._rowState === 'güncellendi' && <span className="diff-badge diff-badge--updated">Güncellendi</span>}
                            </td>
                            <td>{c.course_name}</td>
                            <td>{c.course_type}</td>
                            <td>{c.teorik}</td>
                            <td>{c.uygulama}</td>
                            <td>{c.laboratuvar}</td>
                            <td>{c.kredi}</td>
                            <td>{c.akts}</td>
                          </tr>
                          {c._aciklama && (
                            <tr className="diff-explanation-row">
                              <td colSpan={8} className="diff-explanation">{sanitizePublicText(c._aciklama)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {dc.yeniDersler.length > 0 && (
              <>
                <h3>Önerilen Yeni Plan</h3>
                <div className="diff-table-container">
                  <table className="diff-table">
                    <thead>
                      <tr>
                        <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                        <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dc.yeniDersler.map((c, i) => (
                        <React.Fragment key={c._key || i}>
                          <tr
                            className={
                              c._gorunenDurum === 'yeni'
                                ? 'diff-row diff-row--added'
                                : c._gorunenDurum === 'güncellendi'
                                ? 'diff-row diff-row--updated'
                                : 'diff-row diff-row--same'
                            }
                          >
                            <td>{c.course_code}</td>
                            <td>{c.course_name}</td>
                            <td>{c.course_type}</td>
                            <td>{c.teorik}</td>
                            <td>{c.uygulama}</td>
                            <td>{c.laboratuvar}</td>
                            <td>{c.kredi}</td>
                            <td>{c.akts}</td>
                          </tr>
                          {c._gorunenDurum === 'güncellendi' && c._degisiklikOzeti && (
                            <tr className="diff-explanation-row">
                              <td colSpan={8} className="diff-explanation">
                                {c._degisiklikOzeti}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <ElectiveSuggestionsTable courses={dc.yeniDersler} />

            {yorumByDonem.get(dc.donem) && (
              <div className="curriculum-report__donem-yorum">
                <h4>Dönem Değerlendirmesi</h4>
                <p>{sanitizePublicText(yorumByDonem.get(dc.donem))}</p>
              </div>
            )}
          </section>
        ))}

        <section className="curriculum-report__section">
          <h2>Genel Öneriler</h2>
          <div className="curriculum-report__summary-card">
            <p>{sanitizePublicText(narrative?.genel_oneriler)}</p>
          </div>
        </section>

        <section className="curriculum-report__section">
          <h2>Sonuç</h2>
          <div className="curriculum-report__summary-card">
            <p>{sanitizePublicText(narrative?.sonuc)}</p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CurriculumReportView;
